import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/**
 * Detecta un clic FUERA del elemento referenciado y ejecuta `onOutside`. El listener se registra un
 * tick después (`setTimeout(0)`) para no reaccionar al mismo clic que abrió el panel — mismo patrón
 * ya usado en `Sidebar.tsx` (flyout de Administración) y `LanguageMenu`.
 *
 * `onOutside` se guarda en un ref y se lee siempre actualizado: así el consumidor puede pasar una
 * arrow function inline (`() => setOpen(false)`) sin que el hook reinstale el listener en cada
 * render por un callback con identidad nueva.
 */
export function useClickOutside<T extends HTMLElement>(onOutside: () => void): RefObject<T> {
  const containerRef = useRef<T>(null);
  const onOutsideRef = useRef(onOutside);

  // Se refresca en un efecto y no durante el render: React puede empezar un render, descartarlo y
  // reintentarlo, y una mutación hecha en el render descartado se queda igualmente. Sin array de
  // dependencias corre después de CADA render, que es exactamente lo que hacía antes.
  useEffect(() => {
    onOutsideRef.current = onOutside;
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onOutsideRef.current();
      }
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return containerRef;
}
